'use client'

import type { ColumnDef } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import {
  // Download,
  FileText,
  Receipt,
} from 'lucide-react'

import type { RoyaltyRunningBalanceEntry } from '@package/api'
import { Button } from '@ui/web/components'
import { formatCurrency, formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { RoyaltyRunningBalancePageType } from '../index'

export interface RoyaltyRunningBalanceColumnsOptions {
  type?: RoyaltyRunningBalancePageType
  /** Custom render function for the Report button link. Receives royaltyId and should return a React node. */
  renderReportLink?: (royaltyId: string) => ReactNode
  /** When type is admin, called for PENDING withdrawals to open Pay withdrawal dialog. */
  onPayWithdrawal?: (entry: { withdrawalId: string; amount: number; currency: string }) => void
}

export const getRoyaltyRunningBalanceColumns = (
  options?: RoyaltyRunningBalanceColumnsOptions,
): ColumnDef<RoyaltyRunningBalanceEntry>[] => [
  {
    id: 'date',
    accessorKey: 'date',
    enableHiding: false,
    header: 'Date',
    cell: ({ row }) => {
      return <div className='srd:text-nowrap'>{formatDate(row.original.date, { dateStyle: 'medium' })}</div>
    },
  },
  {
    id: 'description',
    accessorKey: 'description',
    enableHiding: false,
    header: 'Description',
    cell: ({ row }) => {
      const { description } = row.original
      return (
        <div className='srd:flex srd:items-center srd:gap-2'>
          <span>{description}</span>
        </div>
      )
    },
  },
  {
    id: 'amount',
    accessorKey: 'amount',
    enableHiding: false,
    header: () => <span className='srd:text-right srd:w-full srd:block'>Amount</span>,
    cell: ({ row }) => {
      const { amount, currency, type, withdrawalStatus } = row.original
      const isNegative = amount < 0
      const displayAmount = Math.abs(amount)
      const formattedAmount = formatCurrency(displayAmount, {
        currency,
        maximumFractionDigits: 2,
      })

      return (
        <div className='srd:flex srd:items-center srd:justify-end srd:gap-2'>
          {type === 'WITHDRAWAL' && withdrawalStatus === 'PENDING' && (
            <div className='srd:h-4 srd:w-1 srd:rounded srd:bg-main-pink-100' />
          )}

          <span
            className={cn(
              'srd:text-foreground srd:font-semibold srd:text-nowrap',
              isNegative && 'srd:text-neutrals-n-75',
            )}
          >
            {isNegative ? `(${formattedAmount})` : formattedAmount}
          </span>
        </div>
      )
    },
  },
  {
    id: 'action',
    enableHiding: false,
    header: '',
    cell: ({ row }) => {
      const { type, withdrawalStatus, receiptUrl, downloadUrl: _downloadUrl, royaltyId, withdrawalId } = row.original
      const isAdmin = options?.type === 'admin'
      const isPendingWithdrawal = type === 'WITHDRAWAL' && withdrawalStatus === 'PENDING' && withdrawalId
      const hasPayWithdrawal = isAdmin && isPendingWithdrawal && options.onPayWithdrawal

      if (type === 'INITIAL_BALANCE') {
        return null
      }

      return (
        <div className='srd:flex srd:items-center srd:gap-2 srd:w-fit srd:ml-4'>
          {withdrawalStatus && (
            <p
              className={cn(
                'srd:text-xs srd:px-3 srd:py-1 srd:rounded-sm srd:border',
                withdrawalStatus === 'PENDING'
                  ? 'srd:border-main-red-100 srd:text-main-red-100'
                  : 'srd:border-complementary-black srd:text-complementary-black',
              )}
            >
              {withdrawalStatus === 'PAID' ? 'Paid' : withdrawalStatus === 'CANCELLED' ? 'Cancelled' : 'Pending'}
            </p>
          )}

          {hasPayWithdrawal && withdrawalId && (
            <Button
              size='sm'
              className='srd:text-xs srd:h-8'
              onClick={() =>
                options.onPayWithdrawal?.({
                  withdrawalId,
                  amount: Math.abs(row.original.amount),
                  currency: row.original.currency,
                })
              }
            >
              Pay withdrawal
            </Button>
          )}

          {type === 'WITHDRAWAL' && withdrawalStatus === 'PAID' && receiptUrl && (
            <Button size='sm' className='srd:text-xs srd:h-8' variant='outline' asChild>
              <a href={receiptUrl} target='_blank' rel='noopener noreferrer'>
                <Receipt className='srd:size-3 srd:mr-2 srd:text-main-blue-100' />
                Receipt
              </a>
            </Button>
          )}

          {type === 'ROYALTY' && (
            <>
              {royaltyId &&
                (options?.renderReportLink ? (
                  <Button size='sm' className='srd:text-xs srd:h-8' variant='outline' asChild>
                    {options.renderReportLink(royaltyId)}
                  </Button>
                ) : (
                  <Button size='sm' className='srd:text-xs srd:h-8' variant='outline' asChild>
                    <a href={`/royalty/${royaltyId}`}>
                      <FileText className='srd:size-3 srd:mr-2 srd:text-main-blue-100' />
                      Report
                    </a>
                  </Button>
                ))}

              {/* {downloadUrl && (
                <Button
                  size='sm'
                  className='srd:text-xs srd:h-8'
                  variant='outline'
                  onClick={async () => {
                    const res = await fetch(downloadUrl)
                    const blob = await res.blob()
                    const blobUrl = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = blobUrl
                    a.download = `royalty-report.csv`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(blobUrl)
                  }}
                >
                  <Download className='srd:size-3 srd:mr-2 srd:text-main-blue-100' />
                  Download
                </Button>
              )} */}
            </>
          )}
        </div>
      )
    },
  },
]

// Default export for backward compatibility
export const royaltyRunningBalanceColumns = getRoyaltyRunningBalanceColumns()
